/* Questo file contiene una serie di funzioni utili per la stampa delle etichette delle carte e il salvataggio dei
   dati dei relativi products sul db */

import { templates } from "./templatesForPrinter"
import { convertTo24DigitsHexadecimal, randHex } from "./hexadecimal_random_generator";
var base64 = require('base-64');
import {
  compareStrings,
  createFormData,
  ENGLISH_LANGUAGES,
  getGradingNameFromGrade,
  IDEOGRAMS_LANGUAGES,
  isDefined,
  isLanguagePresent,
  isQualifierPresent,
  parseStaticString2Object,
  stringifyGrading,
  titleize
} from "../../../utils/utils";
import {isNil} from "lodash";
var W3CWebSocket = require("websocket").w3cwebsocket;
const PROXY_PORT = "8080";
const convert = require("amrhextotext");

// Costante che riporta il numero massimo di caratteri occidentali stampabili sulle etichette di tipo legacy
export const MAX_CHARS_FOR_LEGACY = 27;

// Costante che riporta il numero massimo di caratteri occidentali stampabili sulle etichette di tipo radiant
export const MAX_CHARS_FOR_RADIANT = 28;

// Costante che riporta il numero massimo di caratteri occidentali stampabili sulle etichette di tipo radiant
// relativamente al nome dei prodotti
export const MAX_CHARS_FOR_PRODUCT_NAME_RADIANT = 40;

// Costante che riporta il numero massimo di caratteri asiatici stampabili sulle etichette di tipo legacy
export const MAX_CHARS_FOR_ASIATIC_LEGACY = 18;

// Costante che riporta il numero massimo di caratteri asiatici stampabili sulle etichette di tipo radiant
export const MAX_CHARS_FOR_ASIATIC_RADIANT = 20;

// Costante che riporta il numero massimo di caratteri asiatici stampabili sulle etichette di tipo radiant
// relativamente al nome dei prodotti
export const MAX_CHARS_FOR_ASIATIC_PRODUCT_NAME_RADIANT = 30;

// Costante che riporta il numero massimo di caratteri stampabili su etichetta legacy per la stringa qualifier
export const MAX_CHARS_FOR_QUALIFIER_LEGACY = 12;

// Costante che riporta il numero massimo di caratteri ammessi per le stringhe da renderizzare a video relative agli
// artworks dei prodotti sigillati
export const MAX_CHARS_FOR_ARTWORKS = 38;

export const noQualifier = { translation: 'none', missing: false }
export const noQualifiers = { left: noQualifier, right: noQualifier }

process.env["NODE_TLS_REJECT_UNAUTHORIZED"] = 0;


// Funzione che calcola il delta complessivo che sussiste tra N parametri di grading valutati e quello che, tra di essi,
// ha il valore minimo
function sumGradingsAndCalculateDelta(minValue, gradingParams) {
  let partialSum = 0.0;
  let partialDelta = 0.0;

  for (const gradingParam of gradingParams) {
    partialSum = partialSum + parseFloat(gradingParam)
    partialDelta = partialDelta + (parseFloat(gradingParam) - minValue)
  }

  return [partialSum, partialDelta]
}


// Funzione che implementa l'algoritmo che determina il grade_overall (cioè il voto finale) di una carta che va
// stampato sulla relativa etichetta al momento del grading della stessa
export function calculateGradeOverallForSingleCard(
  gradeSurfaceFront, gradeSurfaceBack, gradeEdgesFront,
  gradeEdgesBack, gradeCornersFront, gradeCornersBack,
  gradeCenteringFrontLeftRight, gradeCenteringFrontTopBottom,
  gradeCenteringBackLeftRight, gradeCenteringBackTopBottom, setIs10Perfect) {

  const round = (x) => {
    const intPart = Math.floor(x);
    const decimals = x - intPart;

    if (decimals < 0.25) return intPart;
    if (decimals < 0.75) return intPart + 0.5;
    return intPart + 1;
  }

  const calculateSec = (subgrades) => {
    subgrades = subgrades.filter((g) => isDefined(g));
    if (subgrades.length === 0) return '';

    const weight = (subgrade) => 1 / (subgrade ** 5);

    const weightedSubGrades = subgrades.reduce((sum, subgrade) => sum + subgrade * weight(subgrade), 0);
    const totalWeights = subgrades.reduce((sum, subgrade) => sum + weight(subgrade), 0);

    return weightedSubGrades / totalWeights;
  }

  const calculateChi = (centering) => {
    centering = centering.filter((g) => isDefined(g));
    if (centering.length === 0) return '';

    centering = centering.map(g => g?.toLowerCase() === 'na' ? '50_50' : g);

    const mapped = centering.map((c) => {
      const parts = c.split('_');
      return 10 - (Math.abs(parseInt(parts[1]) - parseInt(parts[0])) / 10);
    });

    return mapped.reduce((sum, c) => sum + (1 - (c / 10.0)), 0) / mapped.length;
  }

  const calculateCenteringGradeOverall = (sides) => {
    if (sides.filter(s => !isDefined(s)).length === 0) {
      if (sides.filter(s => isDefined(s) && s !== 'na').length > 0) {
        const grades = sides.map(s => s === 'na' ? 10.0 : s);
        return (grades.reduce((acc, n) => acc + n, 0)) / grades.length;
      }
      else
        return 'na';
    } else
      return '';
  }

  const calculateGradeCentering = (side1, side2) => {
    if (isDefined(side1) && isDefined(side2)) {
      if ([side1, side2].filter(s => isDefined(s) && s.toLowerCase() !== 'na').length > 0) {
        const sides = [side1, side2].filter(s => isDefined(s)).map(s => s.toLowerCase() === 'na' ? '50_50' : s);
        const grades = [];
        for (const side of sides) {
          const delta = Math.abs(parseInt(side.split('_')[0]) - parseInt(side.split('_')[1])) / 10;
          const newGrade = 10 - delta;
          grades.push(newGrade);
        }

        return calculateCenteringGradeOverall(grades);
      } else
        return 'na';
    } else
      return '';
  }

  const sec = calculateSec([gradeSurfaceFront, gradeSurfaceBack, gradeEdgesFront, gradeEdgesBack, gradeCornersFront, gradeCornersBack]);
  const gradeSurfaceOverall = calculateSec([gradeSurfaceFront, gradeSurfaceBack]);
  const gradeEdgesOverall = calculateSec([gradeEdgesFront, gradeEdgesBack]);
  const gradeCornersOverall = calculateSec([gradeCornersFront, gradeCornersBack]);

  let gradeCenteringFront = calculateGradeCentering(gradeCenteringFrontLeftRight, gradeCenteringFrontTopBottom);
  let gradeCenteringBack = calculateGradeCentering(gradeCenteringBackLeftRight, gradeCenteringBackTopBottom);
  let gradeCenteringOverall = calculateCenteringGradeOverall([gradeCenteringFront, gradeCenteringBack]);

  const chi = calculateChi([gradeCenteringFrontLeftRight, gradeCenteringFrontTopBottom, gradeCenteringBackLeftRight, gradeCenteringBackTopBottom]);
  const gradeOverall = isDefined(sec) && isDefined(chi) ? round(sec - chi) : '';

  setIs10Perfect(
    parseFloat(gradeOverall) === 10.0 &&
    parseFloat(gradeSurfaceFront) === 10.0 &&
    parseFloat(gradeSurfaceBack) === 10.0 &&
    parseFloat(gradeEdgesFront) === 10.0 &&
    parseFloat(gradeEdgesBack) === 10.0 &&
    parseFloat(gradeCornersFront) === 10.0 &&
    parseFloat(gradeCornersBack) === 10.0 &&
    ['na', '50_50'].includes(gradeCenteringFrontLeftRight) &&
    ['na', '50_50'].includes(gradeCenteringFrontTopBottom) &&
    ['na', '50_50'].includes(gradeCenteringBackLeftRight) &&
    ['na', '50_50'].includes(gradeCenteringBackTopBottom)
  )

  return [
    gradeOverall,
    gradeCenteringFront,
    gradeCenteringBack,
    isDefined(gradeSurfaceOverall) ? round(gradeSurfaceOverall) : '',
    isDefined(gradeEdgesOverall) ? round(gradeEdgesOverall) : '',
    isDefined(gradeCenteringOverall) && gradeCenteringOverall !== 'na' ? round(gradeCenteringOverall) : '' ,
    isDefined(gradeCornersOverall) ? round(gradeCornersOverall) : '',
    isDefined(sec) ? round(sec) : ''
  ]
}


// Funzione che implementa l'algoritmo che determina il grade_overall (cioè il voto finale) di un booster pack che va
// stampato sulla relativa etichetta al momento del grading dello stesso
export function calculateGradeOverallForBoosterPack(gradeSealedSurface, gradeSealing, gradeFlatness,
                                                    gradeCrimping, gradeHangingHole, setIs10Perfect) {

  // Calcolo il grade_overall in base ai grades surface e sealing
    let gradeSurfaceAndSealing = Math.min(gradeSealedSurface, gradeSealing)
    let partialGradeOverall = gradeSurfaceAndSealing

  // Calcolo l'incremento sul voto parziale dato dal delta
  const sumAndDelta = sumGradingsAndCalculateDelta(gradeSurfaceAndSealing, [gradeSealedSurface, gradeSealing])
  const sumGradings = sumAndDelta[0]
  const delta = sumAndDelta[1]

  if (gradeSurfaceAndSealing === 10)
    partialGradeOverall = 10.5
  else if (gradeSurfaceAndSealing === 9.5 && sumGradings >= 19.5)
    partialGradeOverall = 10.0
  else if (delta >= 1.0)
    partialGradeOverall += 0.5

  // Applico le eventuali decurtazioni determinate dal flatness, dal crimping e dall'hanging hole
  if (gradeFlatness && gradeFlatness.toLowerCase() === 'not_flat')
      partialGradeOverall = partialGradeOverall - 1.0
  if (gradeCrimping && gradeCrimping.toLowerCase() === 'not_regular')
      partialGradeOverall = partialGradeOverall - 0.5
  if (gradeHangingHole && gradeHangingHole.toLowerCase() === 'opened')
      partialGradeOverall = partialGradeOverall - 1.0

  // Verifico se sia un 10 perfetto
  if (partialGradeOverall === 10.5) {
    setIs10Perfect(true);
    partialGradeOverall = 10.0
  }
  else
    setIs10Perfect(false)

  if (partialGradeOverall < 1.0)
    partialGradeOverall = 1.0

  return partialGradeOverall.toFixed(1)
}


// Funzione che implementa l'algoritmo che determina il grade_overall (cioè il voto finale) di un prodotto sigillato
// che va stampato sulla relativa etichetta al momento del grading dello stesso
export function calculateGradeOverallForSealedProduct(gradeSealedSurface, gradeSealing, gradeSealedCorners,
                                                      gradeHangingHole, gradeMaterialCondition, setIs10Perfect) {

  // Calcolo il grade_overall in base ai grades sealed, sealing e corners
  const gradeSealedSealingAndCorners = Math.min(gradeSealedSurface, gradeSealing, gradeSealedCorners)
  let partialGradeOverall = gradeSealedSealingAndCorners

  // Calcolo l'incremento sul voto parziale dato dal delta
  const sumAndDelta = sumGradingsAndCalculateDelta(gradeSealedSealingAndCorners, [gradeSealedSurface, gradeSealing, gradeSealedCorners])
  const sumGradings = sumAndDelta[0]
  const delta = sumAndDelta[1]

  if (gradeSealedSealingAndCorners === 10)
    partialGradeOverall = 10.5
  else if (gradeSealedSealingAndCorners === 9.5 && sumGradings >= 29.5)
    partialGradeOverall = 10.0
  else if (delta >= 2.0)
    partialGradeOverall += 0.5

  // Applico le eventuali decurtazioni determinate dalla material condition e dall'hanging hole
  if (gradeMaterialCondition && gradeMaterialCondition.toLowerCase() === 'damaged')
      partialGradeOverall = partialGradeOverall - 2.0
  if (gradeHangingHole && gradeHangingHole.toLowerCase() === 'opened')
      partialGradeOverall = partialGradeOverall - 1.0

  // Verifico se sia un 10 perfetto
  if (partialGradeOverall === 10.5) {
    setIs10Perfect(true);
    partialGradeOverall = 10.0
  }
  else
    setIs10Perfect(false)

  if (partialGradeOverall < 1.0)
    partialGradeOverall = 1.0

  return partialGradeOverall.toFixed(1)
}


// Funzione che costruisce il nuovo oggetto product da salvare nel db
export function parseProduct2SaveOnDB(currentUserId, currentCompanyId, blueprintImage, productId, owner_id, encasing,
                                      labelType, backofficeNote, isCaseFresh, isAuthentic, isAuthenticWithSubgrades,
                                      isGrading, isGradingWithSubGrades, gradeOverall, is10Perfect,
                                      gradeSurfaceFront, gradeSurfaceBack, gradeEdgesFront, gradeEdgesBack,
                                      gradeCorners, gradeCenteringFrontLeftRight, gradeCenteringFrontTopBottom,
                                      gradeCenteringFront, gradeCenteringBackLeftRight, gradeCenteringBackTopBottom,
                                      gradeCenteringBack, gradeCornersFront, gradeCornersBack, gradeSurfaceOverall,
                                      gradeEdgesOverall, gradeCenteringOverall, gradeCornersOverall, gradeSec,
                                      gradeSealedSurface, gradeSealing, gradeFlatness, gradeCrimping,
                                      gradeHangingHole, gradeSealedCorners, gradeMaterialCondition, gradeContentCompliance,
                                      qualifier, frontImage, backImage, labelLanguage, submission_list_id,
                                      submission_list_in_which_to_insert, supplementary_item_id, mismatched_item_id,
                                      extraInfo, customQualifier, heavyLight, flowpackType,
                                      blueprintInfo2PrintOnLabel, qualifiersToPrintOnLabel, flaws) {


    const new_product = {
        grading: {
            qualifier: qualifier,
            custom_qualifier: isDefined(customQualifier) ? customQualifier : null,
            image: frontImage,
            back_image: backImage,
            label_language: labelLanguage,
            encasing: encasing,
            label_type: labelType,
            grader_id: currentUserId,
            grader_company_id: currentCompanyId,
            submission_list_id: submission_list_id,
            extra_info: extraInfo && extraInfo !== "" ? extraInfo : null,
            is_authentic: isAuthentic,
            is_authentic_with_subgrades: isAuthenticWithSubgrades,
            is_grading_with_subgrades: isGradingWithSubGrades,
            user_id: owner_id
        },
        product: {
            blueprint_image_id: blueprintImage.id,
            blueprint_id: blueprintImage.blueprint_id,
        },
        serial_number: {
            id: null
        },
        item: {
          backoffice_note: backofficeNote ? backofficeNote : null,
          flaws: flaws
        }
    }


    if (compareStrings(blueprintImage.blueprint.category.name, 'booster pack') && heavyLight && heavyLight !== '') {
        new_product['grading'] = {
            ...new_product['grading'],
            heavy_light: heavyLight
        }
    }

    if (compareStrings(blueprintImage.blueprint.category.name, 'booster pack') && flowpackType && flowpackType !== '') {
      new_product['grading'] = {
        ...new_product['grading'],
        flowpack_type: flowpackType
      }
    }

    if (!compareStrings(blueprintImage.blueprint.category.name, 'booster pack')) {
        new_product['grading'] = {
            ...new_product['grading'],
            is_case_fresh: isCaseFresh
        }
    }

    if (!isCaseFresh && !isAuthentic) {
      new_product['grading'] = {
          ...new_product['grading'],
          grade_overall: gradeOverall,
          is_10_perfect: (gradeOverall == 10.0 && is10Perfect)
      }

      if (!isGrading || is10Perfect){
        if (compareStrings(blueprintImage.blueprint.category.name, 'singles')) {
            new_product['grading'] = {
                ...new_product['grading'],
                grade_surface_front: gradeSurfaceFront,
                grade_surface_back: gradeSurfaceBack,
                grade_edges_front: gradeEdgesFront,
                grade_edges_back: gradeEdgesBack,
                grade_corners_front: gradeCornersFront,
                grade_corners_back: gradeCornersBack,
                grade_corners: gradeCorners,
                grade_centering_front_left_right: gradeCenteringFrontLeftRight,
                grade_centering_front_top_bottom: gradeCenteringFrontTopBottom,
                grade_centering_front: gradeCenteringFront,
                grade_centering_back_left_right: gradeCenteringBackLeftRight,
                grade_centering_back_top_bottom: gradeCenteringBackTopBottom,
                grade_centering_back: gradeCenteringBack,
                grade_surface_overall: gradeSurfaceOverall,
                grade_edges_overall: gradeEdgesOverall,
                grade_centering_overall: gradeCenteringOverall,
                grade_corners_overall: gradeCornersOverall,
                grade_sec: gradeSec,
            }
        } else if (compareStrings(blueprintImage.blueprint.category.name, 'booster pack')) {
            new_product['grading'] = {
                ...new_product['grading'],
                grade_sealed_surface: gradeSealedSurface,
                grade_sealing: gradeSealing,
                grade_flatness: gradeFlatness,
                grade_crimping: gradeCrimping,
                grade_hanging_hole: gradeHangingHole
            }
        } else {
            new_product['grading'] = {
              ...new_product['grading'],
              grade_sealed_surface: gradeSealedSurface,
              grade_sealing: gradeSealing,
              grade_sealed_corners: gradeSealedCorners,
              grade_hanging_hole: gradeHangingHole,
              grade_material_condition: gradeMaterialCondition,
              grade_content_compliance: gradeContentCompliance
            }
        }
      }
    }

    if (labelType === 'radiant') {
      new_product['grading'] = {
        ...new_product['grading'],
        ...parseBackLinesToPrintOnLabel(
          compareStrings(blueprintImage.blueprint.category.name, 'singles'),
          compareStrings(blueprintImage.blueprint.category.name, 'booster pack'),
          gradeSurfaceOverall, gradeSealedSurface, gradeSealing,
          gradeEdgesOverall, gradeCornersOverall, gradeFlatness, gradeSealedCorners,
          gradeCenteringOverall, gradeCrimping, gradeHangingHole, gradeMaterialCondition,
          [gradeCenteringFrontLeftRight, gradeCenteringFrontTopBottom,
          gradeCenteringBackLeftRight, gradeCenteringBackTopBottom],
          extraInfo, maxCharsByLocale('en', labelType), isAuthentic, isAuthenticWithSubgrades, isCaseFresh,
          isGrading, isGradingWithSubGrades, is10Perfect, blueprintInfo2PrintOnLabel, qualifiersToPrintOnLabel, qualifier
        )
      }
    }

    if (productId === null) {
        new_product.product['user_id'] = owner_id;
        new_product.product['condition'] = 'used';
        if (submission_list_in_which_to_insert) {
          new_product["submission_list_in_which_to_insert"] = { id: submission_list_in_which_to_insert };

          if (supplementary_item_id) {
            new_product["submission_list_in_which_to_insert"] = {
              ...new_product["submission_list_in_which_to_insert"],
              supplementary_item_id: supplementary_item_id
            };

          } else if (mismatched_item_id) {
            new_product["submission_list_in_which_to_insert"] = {
              ...new_product["submission_list_in_which_to_insert"],
              mismatched_item_id: mismatched_item_id
            };
          }
        }
    }

    return new_product;
}


//Funzione per validare tramite regular expression l'indirizzo IP della stampante
export function isValidIpAddress(printerIpAddress) {
    const ipAddressRegex = RegExp(
        "^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)[.\\d\\w]){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$"
    );
    return printerIpAddress.match(ipAddressRegex);
}


// Funzione che stampa una nuova etichetta di un prodotto gradato
export async function printLabel(mode, newProduct2SaveOnDB, productId, printerIpAddress, proxyIpAddress,
                                 printAlternately, currentUserCompanyName,
                                 game, game_short_name_1, yuGiOhTranslations, category,
                                 released_at, edition, translationsEditions, era, era_short_name_1,
                                 expansion, expansion_short_name_1,
                                 collectorNumber, name, short_name_1, language, labelLanguage,
                                 version, rarity, artwork,
                                 gradeOverall, is10Perfect, qualifiersToPrintOnLabel, gradedBy, sealed_informations,
                                 extraInfo, setFinalGrading, onSuccess, onError, setApplicationState,
                                 isSingleCard, isBoosterPack, isCaseFresh, isAuthentic, isAuthenticWithSubgrades,
                                 isGrading, isGradingWithSubGrades, heavyLight, flowpackType, encasing, labelType) {

    let serialNumber;
    let messageForPrinter;

    if (mode === 'definitive') {
      let serialNumberData = await generateSerialNumber(onError);
      serialNumber = serialNumberData.value
      newProduct2SaveOnDB['serial_number']['id'] = serialNumberData.id
      newProduct2SaveOnDB['serial_number']['value'] = serialNumberData.value
    } else if (mode === 'reprint' && newProduct2SaveOnDB.serial_number?.value) {
      // Per la ristampa, usa il serial number esistente
      serialNumber = newProduct2SaveOnDB.serial_number.value;
    } else {
      // Effettuo la stampa vera e propria dell'etichetta con numero seriale fittizio
      serialNumber = 1234567890;
    }

    // Per la ristampa, costruisco il messageForPrinter direttamente senza passare da buildMessageForPrinter
    if (mode === 'reprint') {
      const epc = convertTo24DigitsHexadecimal(newProduct2SaveOnDB.product.blueprint_image_id);

      // Usa il template_only_serial e sostituisci i placeholder
      let template = templates['template_only_serial'];

      // Formatta il serial number
      const formattedSerial = parseSerialNumberToPrintOnLabel(serialNumber, isSingleCard, encasing, isBoosterPack);
      const paddedSerial = addPaddingToString(formattedSerial, 10);

      // Sostituisci tutti i placeholder con stringhe vuote, tranne LINE2 che contiene il serial
      const print_label_command = template
        .replace("<SEQ_NUMB>", "")
        .replace("<GRADING>", "")
        .replace("<GO>", "")
        .replace("<D>", "")
        .replace("<QUALIFIER>", "")
        .replace("<LINE1>", "")
        .replace("<LINE2>", paddedSerial)
        .replace("<LINE3>", "")
        .replace("<LINE4>", "")
        .replace("<LINE5>", "")
        .replace("<LINE6>", "");

      messageForPrinter = {
        'sato_printer_host': printerIpAddress,
        'epc': epc,
        'write_epc_command': writeEPCcommand(epc),
        'read_tid_and_epc_command': readTIDandEPCcommand(),
        'print_label_command': print_label_command
      };
    } else {
      // Preparo il messaggio di stampa da spedire alla stampante
      messageForPrinter = buildMessageForPrinter(
        printAlternately, newProduct2SaveOnDB, printerIpAddress,
        currentUserCompanyName, game, game_short_name_1, yuGiOhTranslations, category, released_at, edition, translationsEditions,
        era, era_short_name_1, expansion, expansion_short_name_1,
        collectorNumber, name, short_name_1, language, labelLanguage, version, rarity, artwork,
        gradeOverall, is10Perfect, qualifiersToPrintOnLabel, gradedBy, serialNumber, sealed_informations, extraInfo,
        isSingleCard, isBoosterPack, isCaseFresh, isAuthentic, isAuthenticWithSubgrades, isGrading, isGradingWithSubGrades, heavyLight,
        flowpackType, encasing, labelType
      )
    }

    // Effettuo la stampa vera e propria dell'etichetta (sia per Legacy che per Radiant)
    const printerOutputData = await callPrinter(mode, newProduct2SaveOnDB, productId, serialNumber,
      printerIpAddress, proxyIpAddress, messageForPrinter, setFinalGrading, onSuccess, onError, setApplicationState);

    newProduct2SaveOnDB.grading['tid'] = printerOutputData.tid;
    newProduct2SaveOnDB.grading['epc'] = printerOutputData.epc;

    onSuccess('Successful printing!');

    return true;
}


// Funzione per costruire il nuovo messaggio da inoltrare alla stampante con tutti i comandi di stampa da eseguire
function buildMessageForPrinter(printAlternately, newProduct2SaveOnDB, printerIpAddress,
                                currentUserCompanyName, game, game_short_name_1, yuGiOhTranslations, category, released_at,
                                edition, translationsEditions, era, era_short_name_1,
                                expansion, expansion_short_name_1,
                                collectorNumber, name, short_name_1, language, labelLanguage, version, rarity, artwork,
                                gradeOverall, is10Perfect, qualifiersToPrintOnLabel, gradedBy, serialNumber,
                                sealed_informations, extraInfo, isSingleCard, isBoosterPack, isCaseFresh, isAuthentic,
                                isAuthenticWithSubgrades, isGrading, isGradingWithSubGrades, heavyLight, flowpackType, encasing, labelType) {
  // Salvo in una costante l'epc che dovrà essere scritto sull'RFID dell'etichetta codificandolo in esadecimale.
  // IMPORTANTE: il tid e l'epc presenti sull'RFID dell'etichetta vanno codificati in ESADECIMALE a 24 caratteri
  const epc = convertTo24DigitsHexadecimal(newProduct2SaveOnDB.product.blueprint_image_id);

  // Preparo il messaggio da spedire al proxy node con l'ìndirizzo IP della stampante e
  // con i 3 comandi che essa dovrà eseguire.
  // IMPORTANTE: i comandi da eseguire vengono inviati alla stampante sotto forma di normali STRINGHE
  const messageForPrinter = {
    'sato_printer_host': printerIpAddress,
    'epc': epc,
    'write_epc_command': writeEPCcommand(epc),
    'read_tid_and_epc_command': readTIDandEPCcommand(),
    'print_label_command': printLabelcommand(game, game_short_name_1, yuGiOhTranslations, category, released_at, edition, translationsEditions, era, era_short_name_1,
      expansion, expansion_short_name_1, collectorNumber,
      name, short_name_1, language, labelLanguage, version, rarity, artwork,
      gradeOverall, is10Perfect,
      currentUserCompanyName, qualifiersToPrintOnLabel, gradedBy, serialNumber, sealed_informations, extraInfo,
      isSingleCard, isBoosterPack, isCaseFresh, isAuthentic, isAuthenticWithSubgrades, heavyLight,
      flowpackType, encasing, newProduct2SaveOnDB, labelType)
  }
  if (printAlternately) {
    const empty_sealed_informations = {
      'sealed': '',
      'artwork': '',
      'heavy': '',
      'long_crimp': '',
      'short_crimp': '',
      'light': '',
      'case_fresh': '',
      'authentic': ''
    }
    messageForPrinter["print_empty_label_command"] = printLabelcommand(
      "", "", "", "", "", "", {}, "", "", "", "",
      "", "", "", "", null, parseStaticString2Object(""),
      parseStaticString2Object(""), parseStaticString2Object(""),
      "", false, "", noQualifiers, "", "",
      empty_sealed_informations, null, true, true, false, true,
      true, null, null, '', {}, 'legacy');
  }
  return messageForPrinter
}


// Funzione che genera il numero seriale che dovrà essere stampato sull'etichetta
export async function generateSerialNumber(onError) {
  let url = "/backoffice/generate_new_serial_number.json";

  let response = await fetch(url, {
    method: 'GET'
  });
  if (!response.ok) {
    onError("An error occours during generation of label’s serial number!");
    throw Error("An error occours during saving data on database");
  }
  let serialNumberData = await response.json();
  return serialNumberData;
}


// Funzione che salva un prodotto gradato nel db
export async function saveProduct(newProduct2SaveOnDB, productId, setFinalGrading,
                                  frontGradedImage, backGradedImage, onSuccess, onError) {
  const url = "/backoffice/products" + (productId !== null ? '/' + productId : '');

  newProduct2SaveOnDB.grading['graded_image'] = frontGradedImage;
  newProduct2SaveOnDB.grading['graded_back_image'] = backGradedImage;
  
  console.log('NEW PRODUCT TO SAVE ON DATABASE: ', newProduct2SaveOnDB);

  fetch(url + '.json', {
    method: (productId !== null ? 'PUT' : 'POST'),
    body: createFormData(newProduct2SaveOnDB)
  }).then(response => {
    if (!response.ok)
      throw Error("An error occours during saving data on database");
    else
      return response.json()
  }).then(data => {
    try {
      if (productId) {
        setFinalGrading(
          {
            'productId': productId,
            'grading': {
              ...newProduct2SaveOnDB["grading"],
              is_valid: data.is_valid,
              ecasing: data.encasing,
              evaluations: data.evaluations,
              label_type: data.label_type,
              grader_generality: data.grader_generality,
              emitted_at: data.emitted_at,
              state: data.state,
              tid: data.tid,
              label_printed_at: data.label_printed_at,
            },
            'requested_service': data.requested_service,
            'label_type': data.label_type,
            'backoffice_note': data.backoffice_note,
            'flaws': data.flaws,
            'submissionListState': data.submission_list_state,
            'serialNumber': newProduct2SaveOnDB.serial_number.value,
            'image_file_name': data.image_file_name,
            'back_image_file_name': data.back_image_file_name,
            'thumb_image_url': data.thumb_image_url,
            'thumb_back_image_url': data.thumb_back_image_url,
            'xx_large_image_url': data.xx_large_image_url,
            'xx_large_back_image_url': data.xx_large_back_image_url,
            'graded_image_file_name': data.graded_image_file_name,
            'graded_back_image_file_name': data.graded_back_image_file_name,
            'thumb_graded_image_url': data.thumb_graded_image_url,
            'thumb_graded_back_image_url': data.thumb_graded_back_image_url,
            'xx_large_graded_image_url': data.xx_large_graded_image_url,
            'xx_large_graded_back_image_url': data.xx_large_graded_back_image_url
          }
        )
      }
      onSuccess('Data correctly saved on database!')
    } catch {
      onError("An error occours during saving data on database!");
    }
  })
    .catch((error) => {
      onError("An error occours during saving data on database!");
    });
}


// Funzione che invia i comandi di stampa alla stampante per la stampa dell'etichetta
async function callPrinter(mode, newProduct2SaveOnDB, productId, serialNumber, printerIpAddress, proxyIpAddress,
                           messageForPrinter, setFinalGrading, onSuccess, onError, setApplicationState) {
  return new Promise(resolve => {
    // Creo una websocket da connettere al proxy node a cui mandare i comandi di stampa che dovrà girare alla
    // stampante
    var client = new WebSocket('wss://printerproxy.aigrading.com:' + PROXY_PORT + '/', 'echo-protocol');

    console.log('messageForPrinter: ', messageForPrinter)

    // Apro una connessione con il proxy node tramite websocket
    // e su di essa invio i comandi di stampa che dovranno poi essere girati alla stampante dal proxy stesso
    client.onopen = function() {
      console.log('WEBSOCKET CLIENT CONNECTED WITH THE PROXY NODE');
      client.send(JSON.stringify(messageForPrinter));
    };

    client.onclose = function() {
      console.log('WEBSOCKET CLIENT CLOSED');
    };

    // Quando sulla websocket arriva un messaggio dal proxy node, prelevo il tid e l'epc dell'etichetta letti
    // dalla stampante e salvo il nuovo product sul db, tramite una chiamata ajax.
    // Nel caso invece il proxy abbia comunicato che qualcosa è andato storto, mostro all'utente
    // una modale con il testo dell'errore
    client.onmessage = function(e) {
      if (e.data.startsWith('Error: ')) {
        onError("Unable to connect proxy to the printer!");
        client.close();
      } else {
        const returnedData = JSON.parse(e.data);

        console.log('RETURNED TID: ', returnedData.tid);
        console.log('RETURNED EPC: ', returnedData.epc);

        if (mode === 'definitive' || mode === 'reprint') {
          resolve(returnedData);
        } else {
          setApplicationState('ready');
        }

        // Infine chiudo la connessione col proxy aperta per eseguire la stampa
        client.close();
      }
    };

    client.onerror = function(error) {
      onError('Unable to connect web app to the proxy!')
    };
  });
}


// Funzione che restituisce la stringa che codifica il comando che dovrà eseguire la stampante per scrivere l'epc
// sull'RFID dell'etichetta
function writeEPCcommand(epc) {
    return (
        String.fromCharCode(2, 27) +
        "A" +
        String.fromCharCode(27) +
        "IP0e:h,epc:" +
        epc +
        ";" +
        String.fromCharCode(27) +
        "Z" +
        String.fromCharCode(3)
    );
}


// Funzione che restituisce la stringa che codifica il comando che dovrà eseguire la stampante per leggere il tid e
// l'epc dall'RFID dell'etichetta
function readTIDandEPCcommand() {
    return String.fromCharCode(18) + "PJ,U1,U2,NO";
}


// Funzione che restituisce la stringa che codifica il comando che dovrà eseguire la stampante per effettuare la
// stampa vera e propria dell'etichetta
function printLabelcommand(game_name, game_short_name_1, yuGiOhTranslations, category, released_at, edition,
                           translationsEditions, era_name, era_short_name_1,
                           expansion_name, expansion_short_name_1, collectorNumber,
                           name, short_name_1, language, labelLanguage, version, rarity, artwork,
                           gradeOverall, is10Perfect, graderCompany, qualifiersToPrintOnLabel, gradedBy, serialNumber,
                           sealed_informations, extraInfo, isSingleCard, isBoosterPack, isCaseFresh, isAuthentic,
                           isAuthenticWithSubgrades, heavyLight, flowpackType, encasing, newProduct2SaveOnDB, labelType) {

    // Per etichette Radiant: stampa solo il serial number (template minimale)
    let template_name = 'template_only_serial';

    // Logica esistente per etichette Legacy
    const no_rarity_editions = Array.isArray(translationsEditions?.['No Rarity']) ? translationsEditions['No Rarity'] : []
    const product = parseStaticString2Object(name, short_name_1 ? short_name_1 : '');
    const game = parseStaticString2Object(game_name, game_short_name_1 ? game_short_name_1 : '');
    const era = parseStaticString2Object(era_name, era_short_name_1 ? era_short_name_1 : '')
    const expansion = parseStaticString2Object(expansion_name, expansion_short_name_1 ? expansion_short_name_1 : '')
    const maxChars = maxCharsByLocale(labelLanguage, labelType);
    const maxCharsForProductName = maxCharsByLocale(labelLanguage, labelType, 'productname');
    const isYuGiOh = game_name ? yuGiOhTranslations.includes(game_name.toLowerCase()) : false;
    const parsedGradeOverall = parseGradeOverallToPrintOnLabel(isCaseFresh, isAuthentic || isAuthenticWithSubgrades, gradeOverall);
    const evaluation = parseGradingNameToPrintOnLabel(isCaseFresh, isAuthentic || isAuthenticWithSubgrades, gradeOverall, is10Perfect);
    const qualifier = isQualifierPresent(qualifiersToPrintOnLabel.left.translation) ? qualifiersToPrintOnLabel.left.translation : qualifiersToPrintOnLabel.right.translation
    newProduct2SaveOnDB['grading'] = newProduct2SaveOnDB['grading'] ? newProduct2SaveOnDB['grading'] : {};

    if(!labelType == 'radiant'){
      template_name =
        "template_" +
        (game_name === 'Pokémon' ? 'pk' : (['ポケモンカードゲーム', '포켓몬 카드 게임', '寶可夢 / 宝可梦'].includes(game_name) || IDEOGRAMS_LANGUAGES.includes(labelLanguage) ? 'pkjp' : 'pk')) +
        (['10', '10.0'].includes(gradeOverall.toString()) ? '_10_grading' :
          (gradeOverall.toString().indexOf('.0') > -1 ||
          gradeOverall.toString().indexOf('.') === -1 ? "_integer_grading" : "_floating_grading")
        );

      console.log('template_name: ', template_name)  
    }
    let template = templates[template_name];


    if (isSingleCard) {
      // Se l'edizione della carta è 'No Rarity',
      // allora questa viene stampata sull'etichetta al posto della rarità della carta
      if (isDefined(edition) && isDefined(edition.meta_name) && no_rarity_editions.includes(edition.meta_name))
          rarity = edition

      // Template di stampa per CARTE SINGOLE - ETICHETTE LEGACY
      newProduct2SaveOnDB['grading']['label_line_1'] = parseGameReleaseAndLanguageToPrintOnLabel(game, released_at, language, maxChars);
      newProduct2SaveOnDB['grading']['label_line_2'] = !isYuGiOh ? (isDefined(expansion.meta_name) || isDefined(expansion.short_name_1) ? parseExpansionAndEditionToPrintOnLabel(expansion, edition, translationsEditions, maxChars) : parseEraToPrintOnLabel(era, maxChars)) : parseCollectorNumberAndEditionToPrintOnLabel(collectorNumber, edition, maxChars);
      newProduct2SaveOnDB['grading']['label_line_3'] = parseProductNameAndCollectorNumberToPrintOnLabel(product, collectorNumber, game_name, yuGiOhTranslations, maxCharsForProductName);
      newProduct2SaveOnDB['grading']['label_line_4'] = parseVersionAndRarityToPrintOnLabel(version, rarity, maxChars);
      newProduct2SaveOnDB['grading']['label_line_5'] = parseExtraInfoToPrintOnLabel(extraInfo, maxChars);
      newProduct2SaveOnDB['grading']['label_line_6'] = parseGraderToPrintOnLabel(gradedBy, graderCompany, maxChars) !== '' ? parseGraderToPrintOnLabel(gradedBy, graderCompany, maxChars)  :
        (isAuthentic || isAuthenticWithSubgrades ? addPaddingToAuthenticOrCaseFresh(parseAuthenticToPrintOnLabel(sealed_informations[isAuthentic ? 'authentic' : 'authentic_with_subgrades'], qualifier, 'legacy', labelLanguage), template_name) : '');

    } else {
      // Template di stampa per PRODOTTI SIGILLATI - ETICHETTE LEGACY
      newProduct2SaveOnDB['grading']['label_line_1'] = parseGameReleaseAndLanguageToPrintOnLabel(game, released_at, language, maxChars);
      newProduct2SaveOnDB['grading']['label_line_2'] = (isDefined(expansion.meta_name) || isDefined(expansion.short_name_1) ? parseExpansionAndEditionToPrintOnLabel(expansion, edition, translationsEditions, maxChars) : ((isDefined(era.meta_name) || isDefined(era.short_name_1)) ? parseEraToPrintOnLabel(era, maxChars) : parseProductNameAndCollectorNumberToPrintOnLabel(product, collectorNumber, game_name, yuGiOhTranslations, maxCharsForProductName)));
      newProduct2SaveOnDB['grading']['label_line_3'] = parseSealedCategoryToPrintOnLabel(category, flowpackType, maxChars, sealed_informations);
      newProduct2SaveOnDB['grading']['label_line_4'] = parseSealedArtworkToPrintOnLabel(artwork, maxChars);
      newProduct2SaveOnDB['grading']['label_line_5'] = parseExtraInfoAndHeavyLightToPrintOnLabel(extraInfo, heavyLight, sealed_informations, maxChars);
      newProduct2SaveOnDB['grading']['label_line_6'] = parseGraderToPrintOnLabel(gradedBy, graderCompany, maxChars) !== '' ? parseGraderToPrintOnLabel(gradedBy, graderCompany, maxChars) :
        (isCaseFresh || isAuthentic || isAuthenticWithSubgrades ? addPaddingToAuthenticOrCaseFresh(parseSealedCaseFreshAuthenticToPrintOnLabel(isCaseFresh, sealed_informations['case_fresh'], sealed_informations[isAuthentic ? 'authentic' : 'authentic_with_subgrades'], qualifier), template_name) : '')
    }


    if(labelType == 'radiant'){
      // Sostituisci tutti i placeholder con stringhe vuote, tranne SEQ_NUMB
      template = template
        .replace("<SEQ_NUMB>", "")
        .replace("<GRADING>", "")
        .replace("<GO>", "")
        .replace("<D>", "")
        .replace("<QUALIFIER>", "")
        .replace("<LINE1>", "")
        .replace("<LINE2>", addPaddingToString(parseSerialNumberToPrintOnLabel(serialNumber, isSingleCard, encasing, isBoosterPack), 10))
        .replace("<LINE3>", "")
        .replace("<LINE4>", "")
        .replace("<LINE5>", "")
        .replace("<LINE6>", "");
    } else {
      template = template
        .replace("<SEQ_NUMB>", parseSerialNumberToPrintOnLabel (serialNumber, isSingleCard, encasing, isBoosterPack))
        .replace("<GRADING>", addPaddingToGradingName(evaluation))
        .replace("<GO>", parsedGradeOverall[0])
        .replace("<D>", parsedGradeOverall[1])
        .replace("<QUALIFIER>", addPaddingToQualifier(parseQualifierToPrintOnLabel(isCaseFresh, isAuthentic || isAuthenticWithSubgrades, qualifier, maxCharsForQualifier('legacy')), template_name))
        .replace("<LINE1>", newProduct2SaveOnDB['grading']['label_line_1'])
        .replace("<LINE2>", newProduct2SaveOnDB['grading']['label_line_2'])
        .replace("<LINE3>", newProduct2SaveOnDB['grading']['label_line_3'])
        .replace("<LINE4>", newProduct2SaveOnDB['grading']['label_line_4'])
        .replace("<LINE5>", newProduct2SaveOnDB['grading']['label_line_5'])
        .replace("<LINE6>", newProduct2SaveOnDB['grading']['label_line_6'])
    }
    
    
    if (labelType === 'radiant') {
      if (isSingleCard) {
        // Template di stampa per CARTE SINGOLE - ETICHETTE RADIANT
        newProduct2SaveOnDB['grading']['label_line_1'] = parseCollectorNumberToPrintOnLabel(collectorNumber, null, [], true);
        newProduct2SaveOnDB['grading']['label_line_2'] = parseGameReleaseAndLanguageToPrintOnLabel(game, released_at, language?.toUpperCase(), maxChars, true);
        newProduct2SaveOnDB['grading']['label_line_3'] = (isDefined(expansion.meta_name) || isDefined(expansion.short_name_1) ? parseExpansionAndEditionToPrintOnLabel(expansion, edition, translationsEditions, maxChars, true) : parseEraToPrintOnLabel(era, maxChars, true));
        newProduct2SaveOnDB['grading']['label_line_4'] = parseVersionAndRarityToPrintOnLabel(version, rarity, maxChars, true);
        newProduct2SaveOnDB['grading']['label_line_5'] = parseQualifierToPrintOnLabel(false, false, qualifiersToPrintOnLabel.left.translation, maxChars, true);
        newProduct2SaveOnDB['grading']['label_line_6'] = parseProductNameAndCollectorNumberToPrintOnLabel(product, null, null, [], maxCharsForProductName, true);

      } else {
        // Template di stampa per PRODOTTI SIGILLATI - ETICHETTE RADIANT
        newProduct2SaveOnDB['grading']['label_line_1'] = parseGameReleaseAndLanguageToPrintOnLabel(game, released_at, language?.toUpperCase(), maxChars, true);
        newProduct2SaveOnDB['grading']['label_line_2'] = parseSealedCategoryToPrintOnLabel(category, flowpackType, maxChars, sealed_informations, true);
        newProduct2SaveOnDB['grading']['label_line_3'] = parseSealedArtworkToPrintOnLabel(artwork, maxChars, true);
        newProduct2SaveOnDB['grading']['label_line_4'] = parseExtraInfoAndHeavyLightToPrintOnLabel(extraInfo, heavyLight, sealed_informations, maxChars);
        newProduct2SaveOnDB['grading']['label_line_5'] = parseQualifierToPrintOnLabel(false, false, qualifiersToPrintOnLabel.left.translation, maxChars, true);
        newProduct2SaveOnDB['grading']['label_line_6'] = (isDefined(expansion.meta_name) || isDefined(expansion.short_name_1) ? parseExpansionAndEditionToPrintOnLabel(expansion, edition, translationsEditions, maxCharsForProductName, true) : ((isDefined(era.meta_name) || isDefined(era.short_name_1)) ? parseEraToPrintOnLabel(era, maxCharsForProductName, true) : parseProductNameAndCollectorNumberToPrintOnLabel(product, null, null, [], maxCharsForProductName, true)));
      }
    }

    return template;
}





// PARSER DELLE STRINGHE DA STAMPARE NELLE ETICHETTE AIGRADING (INDIPENDENTEMENTE DAL LORO TIPO)

// Funzione che aggiunge degli spazi vuoti davanti alla stringa desiderata per portare il testo centrale
function addPaddingToString(string, number){
  return string.padStart(number, ' ')
} 

// Funzione che aggiunge degli spazi vuoti davanti alle stringa AUTHENTIC e CASE FRESH da stampare (eventualmente)
// in etichetta
function addPaddingToAuthenticOrCaseFresh(authentic, template_name) {
    if (template_name.indexOf('pkjp') > -1) {
        return authentic.padStart(40, ' ')
    } else {
        return authentic.padStart(authentic.length + Math.floor((43 - authentic.length) / 2), ' ')
    }
}


// Funzione che aggiunge degli spazi vuoti davanti alla nomenclatura del grading da stampare
// nell'etichetta
function addPaddingToGradingName(gradingName) {
    return (isDefined(gradingName) ? gradingName.padStart(gradingName.length + Math.ceil((12 - gradingName.length) / 2), ' ') : '');
}


// Funzione che aggiunge degli spazi vuoti davanti alla stringa che riporta il qualifier della carta o prodotto sealed
// da stampare in etichetta
function addPaddingToQualifier(qualifier, template_name) {
    if (isQualifierPresent(qualifier)) {
        if (template_name.indexOf('pkjp') > -1) {
            switch (qualifier.length) {
                case 2:
                    qualifier = qualifier.padStart(15, ' ')
                    break;
                case 3:
                    qualifier = qualifier.padStart(14, ' ')
                    break;
                case 4:
                    qualifier = qualifier.padStart(14, ' ')
                    break;
                case 5:
                    qualifier = qualifier.padStart(13, ' ')
                    break;
                case 6:
                    qualifier = qualifier.padStart(12, ' ')
                    break;
                case 7:
                    qualifier = qualifier.padStart(12, ' ')
                    break;
                case 8:
                    qualifier = qualifier.padStart(14, ' ')
                  break;
                case 9:
                  qualifier = qualifier.padStart(16, ' ')
                  break;
                case 10:
                  qualifier = qualifier.padStart(16, ' ')
                  break;
                case 11:
                  qualifier = qualifier.padStart(16, ' ')
                  break;
                case 12:
                  qualifier = qualifier.padStart(16, ' ')
                    break;
                default:
                    break;
            }
        }
        else if (template_name.indexOf('pk') > -1) {
            qualifier = qualifier.padStart(qualifier.length + Math.floor((12 - qualifier.length) / 2), ' ')
        }

        return qualifier;
    } else {
        return '';
    }
}


// Funzione che costruisce la stringa composta dal numero seriale della carta / prodotto sealed gradato che andrà
// stampata in etichetta
export function parseSerialNumberToPrintOnLabel (serialNumber, isSingleCard, encasing, isBoosterPack) {
    if (isDefined(serialNumber)) {
        if (isSingleCard) {
            return ((encasing === 'sleeve' ? 'A' : 'B') + serialNumber.toString().padStart(9, "0"))
        } else {
            return ((isBoosterPack ? 'D' : 'C') + serialNumber.toString().padStart(9, "0"))
        }
    } else {
        return '';
    }
}


// Funzioni che costruiscono la stringa composta da game, anno di release e lingua del prodotto che andrà stampata
// sull'etichetta
export const parseGameReleaseYearAndLanguage = (gameName, releaseYear, language, printRelease, printLanguage, notUpperCase) => {
  const shortLanguage = language && language.length > 2 ? language.slice(0, 2) : language
  const parsedLanguage = ENGLISH_LANGUAGES.includes(shortLanguage) ? 'EN' : (isLanguagePresent(shortLanguage) ? shortLanguage : '')
  const parsedReleaseYear = releaseYear && releaseYear !== 'Promo' ? releaseYear.toString().substring(0, 4) : '';

  if (printRelease && printLanguage)
      return finalParse(gameName + (parsedReleaseYear ? ' ' + parsedReleaseYear : '') + (parsedLanguage ? ' ' + parsedLanguage : ''), notUpperCase);

  if (printRelease)
    return finalParse(gameName + (parsedReleaseYear ? ' ' + parsedReleaseYear : ''), notUpperCase);

  if (printLanguage)
    return finalParse(gameName + (parsedLanguage ? ' ' + parsedLanguage : ''), notUpperCase);

  return finalParse(gameName, notUpperCase);
}





// PARSER DELLE STRINGHE DA STAMPARE NELLE VECCHIE ETICHETTE AIGRADING (QUELLE DI TIPO LEGACY)

export function parseGameReleaseAndLanguageToPrintOnLabel (game, releaseYear, language, maxChars, notUpperCase) {
    if (game.meta_name) {
      let parsedString = parseGameReleaseYearAndLanguage(game.meta_name, releaseYear, language, true, true, notUpperCase)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    if (game.short_name_1) {
      let parsedString = parseGameReleaseYearAndLanguage(game.short_name_1, releaseYear, language, true, true, notUpperCase)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    if (game.meta_name) {
      let parsedString = parseGameReleaseYearAndLanguage(game.meta_name, releaseYear, language, true, false, notUpperCase)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    if (game.meta_name) {
      let parsedString = parseGameReleaseYearAndLanguage(game.meta_name, releaseYear, language, false, true, notUpperCase)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    if (game.meta_name) {
      let parsedString = parseGameReleaseYearAndLanguage(game.meta_name, releaseYear, language, false, false, notUpperCase)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    if (game.short_name_1) {
      let parsedString = parseGameReleaseYearAndLanguage(game.short_name_1, releaseYear, language, true, false, notUpperCase)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    if (game.short_name_1) {
      let parsedString = parseGameReleaseYearAndLanguage(game.short_name_1, releaseYear, language, false, true, notUpperCase)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    if (game.short_name_1) {
      let parsedString = parseGameReleaseYearAndLanguage(game.short_name_1, releaseYear, language, false, false, notUpperCase)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    return parseGameReleaseYearAndLanguage(game.meta_name, releaseYear, language, false, false, notUpperCase).substring(0, maxChars)
}


// Funzione che costruisce la stringa composta da espansione ed edizione che andrà stampata sull'etichetta
export function parseExpansionAndEditionToPrintOnLabel (expansion, edition, translationsEditions, maxChars, notUpperCase) {
  if ((!expansion || (!expansion.meta_name && !expansion.short_name_1)) &&
    (!edition || (!edition.meta_name && !edition.short_name_1)))
    return ''

  else {
    const no_rarity_editions = Array.isArray(translationsEditions?.['No Rarity']) ? translationsEditions['No Rarity'] : []
    const first_editions = Array.isArray(translationsEditions?.['1 Edition']) ? translationsEditions['1 Edition'] : []
    const unlimited_editions = Array.isArray(translationsEditions?.['Unlimited']) ? translationsEditions['Unlimited'] : []

    const writeEditionAfterExpansion = (expansion_name_string, edition_string) => {
      return (
        edition_string &&
        !no_rarity_editions.includes(edition_string) &&
        expansion_name_string.toLowerCase().indexOf(edition_string.toLowerCase()) === -1 &&
        !(first_editions.includes(edition_string) && (
          expansion_name_string.toLowerCase().indexOf('1st edition') > -1 ||
          expansion_name_string.toLowerCase().indexOf('1a edizione') > -1 ||
          expansion_name_string.toLowerCase().indexOf('1 edition') > -1 ||
          expansion_name_string.toLowerCase().indexOf('1 edizione') > -1 ||
          expansion_name_string.toLowerCase().indexOf('1st') > -1 ||
          expansion_name_string.toLowerCase().indexOf('1a') > -1 ||
          expansion_name_string.toLowerCase().indexOf('1ed') > -1
        )))
    }

    const expansionNameAndEdition = (expansion_name_string, edition_string) => {
      let parsedExpansion = expansion_name_string

      // Stampo l'edizione a fianco al nome dell'espansione se l'edizione è effettivamente presente, non è "No Rarity" e
      // se il nome dell'espansione non contiene già al suo interno l'edizione stessa
      if (writeEditionAfterExpansion(parsedExpansion, edition_string)) {

        // Se il nome dell'espansione contiene al suo interno la stringa Unlimited, ma l'edizione della carta è
        // 1 Edition, allora tolgo la stringa Unlimited dal nome dell'espansione e stampo l'edizione 1 Edition a
        // fianco a nome dell'espansione così modificato
        if (first_editions.includes(edition_string)) {
          for (const unl_edition of unlimited_editions)
            if (unl_edition && (parsedExpansion.toLowerCase().indexOf(' ' + unl_edition.toLowerCase() + ' ') > -1 || parsedExpansion.toLowerCase().endsWith(' ' + unl_edition.toLowerCase()))) {
              parsedExpansion = parsedExpansion.toLowerCase().replace(' ' + unl_edition.toLowerCase() + ' ', "").replace(' ' + unl_edition.toLowerCase(), "");
              break;
            }
        }

        parsedExpansion = parsedExpansion + ' ' + edition_string;
      }

      return finalParse(parseSpecialSymbols(parsedExpansion, maxChars), notUpperCase).replace('1ST', '1st').replace('1A', '1a');
    }


    let parsedString = expansionNameAndEdition(expansion.meta_name, edition.meta_name)
    if (parsedString.length <= maxChars)
      return parsedString

    if (edition.short_name_1) {
      parsedString = expansionNameAndEdition(expansion.meta_name, edition.short_name_1);
      if (parsedString.length <= maxChars)
        return parsedString;
    }

    if (expansion.short_name_1) {
      parsedString = expansionNameAndEdition(expansion.short_name_1, edition.meta_name);
      if (parsedString.length <= maxChars)
        return parsedString;
    }

    if (expansion.short_name_1 && edition.short_name_1) {
      parsedString = expansionNameAndEdition(expansion.short_name_1, edition.short_name_1);
      if (parsedString.length <= maxChars)
        return parsedString;
    }

    if (edition.short_name_1) {
      if (writeEditionAfterExpansion(expansion.meta_name, edition.short_name_1))
        return expansionNameAndEdition(expansion.meta_name.substring(0, maxChars - (edition.short_name_1.length + 1)), edition.short_name_1).substring(0, maxChars);
      else
        return expansionNameAndEdition(expansion.meta_name, edition.short_name_1).substring(0, maxChars);
    }

    if (expansion.short_name_1) {
      if (writeEditionAfterExpansion(expansion.short_name_1, edition.meta_name))
        return expansionNameAndEdition(expansion.short_name_1.substring(0, maxChars - (edition.meta_name.length + 1)), edition.meta_name).substring(0, maxChars);
      else
        return expansionNameAndEdition(expansion.short_name_1, edition.meta_name).substring(0, maxChars);
    }

    return expansionNameAndEdition(expansion.meta_name, edition.meta_name).substring(0, maxChars)
  }
}


// Funzione che costruisce la stringa composta da card number e edizione di una carta che andrà stampata sull'etichetta
export function parseCollectorNumberAndEditionToPrintOnLabel (collectorNumber, edition, maxChars) {
  if (!collectorNumber && (!edition || (!edition.meta_name && !edition.short_name_1)))
    return ''
  else {
    const collectorNumberAndEdition = (collector_number_string, edition_string) => {
      return (collector_number_string + ' ' + edition_string).toUpperCase()
    }

    if (!collectorNumber)
      collectorNumber = ''

    if (edition.meta_name) {
      let parsedString = collectorNumberAndEdition(collectorNumber, edition.meta_name)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    if (edition.short_name_1) {
      let parsedString = collectorNumberAndEdition(collectorNumber, edition.short_name_1);
      if (parsedString.length <= maxChars)
        return parsedString;
    }

    if (edition.short_name_1)
      return collectorNumberAndEdition(collectorNumber, edition.short_name_1).substring(0, maxChars);

    if (edition.meta_name)
      return collectorNumberAndEdition(collectorNumber, edition.meta_name).substring(0, maxChars);

    return collectorNumber.toUpperCase().substring(0, maxChars)
  }
}


// Funzione che costruisce la stringa composta dal nome dell'era del prodotto che andrà stampata sull'etichetta
export function parseEraToPrintOnLabel (era, maxChars, notUpperCase) {
  if (era && era.meta_name) {
    if (era.meta_name.length <= maxChars)
      return finalParse(era.meta_name, notUpperCase);
    else {
      if (era.short_name_1)
        return finalParse(era.short_name_1, notUpperCase).substring(0, maxChars);
      else
        return finalParse(era.meta_name, notUpperCase).substring(0, maxChars);
    }
  }
  else
    return ''
}


// Funzione che costruisce la stringa composta dal numero di una carta che andrà stampata in etichetta
export function parseCollectorNumberToPrintOnLabel (collectorNumber, game, yuGiOhTranslations, trim) {
  if (isDefined(collectorNumber) && !yuGiOhTranslations.includes(game?.toLowerCase())) {
    const parsedString = (collectorNumber.indexOf("/") === -1 && collectorNumber.indexOf("-") === -1 && collectorNumber.indexOf("#") === -1 ? " #" : " ") + collectorNumber;
    return trim ? parsedString.trim() : parsedString;
  }
  else return '';
}


// Funzione che costruisce la stringa composta dal nome del prodotto e dall'eventuale card number
// che andrà stampata in etichetta
export function parseProductNameAndCollectorNumberToPrintOnLabel (product, collectorNumber, game, yuGiOhTranslations, maxChars, notUpperCase) {
  if ((!product || (!product.meta_name && !product.short_name_1)) && !collectorNumber)
    return ''
  else {
    const productNameAndCollectorNumber = (productName, collectorNumber) => {
      if (isDefined(productName)) {
        const name = parseSpecialSymbols(productName, maxChars);
        if (isDefined(collectorNumber)) {
          const parsedCollectorNumber = parseCollectorNumberToPrintOnLabel(collectorNumber, game, yuGiOhTranslations);
          return finalParse(name + parsedCollectorNumber, notUpperCase);
        } else return finalParse(name, notUpperCase);
      } else {
        return parseCollectorNumberToPrintOnLabel(collectorNumber, game, yuGiOhTranslations);
      }
    }

    let parsedString = productNameAndCollectorNumber(product.meta_name, collectorNumber)
    if (parsedString.length <= maxChars)
      return parsedString;

    if (product.short_name_1) {
      parsedString = productNameAndCollectorNumber(product.short_name_1, collectorNumber);
      if (parsedString.length <= maxChars)
        return parsedString;
    }

    return productNameAndCollectorNumber(product.meta_name, collectorNumber).substring(0, maxChars);
  }
}


// Funzione che rimpiazza i simboli speciali con delle lettere alfabetiche per la stampa in etichetta
export function parseSpecialSymbols (inputString, maxChars) {
  if (inputString) {
    // Nel caso delle etichette legacy con stampa dei caratteri non occidentali, il font usato non supporta la stampa
    // dei simboli speciali, quindi in tal caso tali simboli vengono rimpiazzati con appositi caratteri sostitutivi.
    // In tutti gli altri casi (stampe in caratteri non occidentali / etichette non legacy / ecc.), i font usati
    // supportano la stampa dei simboli speciali, quindi tale problema non sussiste e di conseguenzq non vengono
    // fatti replace
    if (maxChars === MAX_CHARS_FOR_LEGACY)
      return (inputString.replaceAll("♂", "M").replaceAll("♀", "F").replaceAll("Δ", "D").replaceAll("★", " ").replaceAll("・", " ")).trim();
    else
      return inputString.trim();
  } else {
    return '';
  }
}


// Funzione che costruisce la stringa composta dalla nomenclatura del grading che andrà stampata sull'etichetta
export function parseGradingNameToPrintOnLabel (isCasefresh, isAuthentic, gradeOverall, is10Perfect) {
  return (!isCasefresh && !isAuthentic ? getGradingNameFromGrade(gradeOverall.toString(), is10Perfect)[1].toUpperCase() : isAuthentic ? 'AUTHENTIC' : '')
}


// Funzione che costruisce la stringa composta da versione e rarità di una carta che andrà stampata sull'etichetta
export function parseVersionAndRarityToPrintOnLabel (version, rarity, maxChars, notUpperCase) {
  const versionAndRarity = (version_string, rarity_string) => {
    return finalParse(
      (
        (isDefined(version_string) && isDefined(rarity_string) ? (version_string + ' - ' + rarity_string) :
        (isDefined(version_string) ? version_string : (isDefined(rarity_string) ? rarity_string : '')))
      ),
    notUpperCase);
  }

  let parsedString = versionAndRarity(version.meta_name, rarity.meta_name)
  if (parsedString.length <= maxChars)
    return parsedString

  if (version.short_name_1) {
    parsedString = versionAndRarity(version.short_name_1, rarity.meta_name);
    if (parsedString.length <= maxChars)
      return parsedString;
  }

  if (rarity.short_name_1) {
    parsedString = versionAndRarity(version.meta_name, rarity.short_name_1);
    if (parsedString.length <= maxChars)
      return parsedString;
  }

  if (version.short_name_1 && rarity.short_name_1) {
    parsedString = versionAndRarity(version.short_name_1, rarity.short_name_1);
    if (parsedString.length <= maxChars)
      return parsedString;
  }

  if (version.short_name_1 && rarity.short_name_1)
    return versionAndRarity(version.short_name_1, rarity.short_name_1).substring(0, maxChars)

  if (version.short_name_1)
    return versionAndRarity(version.short_name_1, rarity.meta_name).substring(0, maxChars)

  if (rarity.short_name_1)
    return versionAndRarity(version.meta_name, rarity.short_name_1).substring(0, maxChars)

  return versionAndRarity(version.meta_name, rarity.meta_name).substring(0, maxChars)
}


// Funzione che costruisce la stringa composta dalla categoria e dal flowpack type di un prodotto sealed
// che andrà stampata sull'etichetta
export function parseSealedCategoryToPrintOnLabel (category, flowpackType, maxChars, sealed_informations, notUpperCase) {
  if (!flowpackType || flowpackType.toLowerCase() === 'none')
    return finalParse(category, notUpperCase).substring(0, maxChars)
  else {
    const categoryAndFlowpackType = (category_string, flowpack_type_string) => {
      return finalParse(category_string + ' ' + flowpack_type_string, notUpperCase);
    }

    const flowpack_type = sealed_informations[flowpackType] ? sealed_informations[flowpackType] : parseStaticString2Object('')

    if (flowpack_type.meta_name) {
      let parsedString = categoryAndFlowpackType(category, flowpack_type.meta_name)
      if (parsedString.length <= maxChars)
        return parsedString
    }

    if (flowpack_type.short_name_1) {
      let parsedString = categoryAndFlowpackType(category, flowpack_type.short_name_1);
      if (parsedString.length <= maxChars)
        return parsedString;
    }

    if (flowpack_type.short_name_1)
      return categoryAndFlowpackType(category, flowpack_type.short_name_1).substring(0, maxChars);

    if (flowpack_type.meta_name)
      return categoryAndFlowpackType(category, flowpack_type.meta_name).substring(0, maxChars);

    return finalParse(category, notUpperCase).substring(0, maxChars)
  }
}


// Funzione che costruisce la stringa composta dall'artwork' di un prodotto sealed che andrà stampata sull'etichetta
export function parseSealedArtworkToPrintOnLabel (artwork, maxChars, notUpperCase) {
    if (artwork && artwork.meta_name) {
        if (artwork.meta_name.length <= maxChars)
            return finalParse(artwork.meta_name, notUpperCase);
        else {
          if (artwork.short_name_1)
            return finalParse(artwork.short_name_1, notUpperCase).substring(0, maxChars);
          else
            return finalParse(artwork.meta_name, notUpperCase).substring(0, maxChars);
        }
    }
    else
        return ''
}


// Funzione che costruisce la stringa con il gradatore del prodotto che andrà stampata sull'etichetta
export function parseGraderToPrintOnLabel (gradedBy, graderCompany, maxChars) {
    if (isDefined(graderCompany)) {
        if (graderCompany.toUpperCase().indexOf('AIGRADING') === -1) {
          if ((gradedBy + ': ' + graderCompany).trim().length <= maxChars)
            return (gradedBy + ': ' + graderCompany).trim().toUpperCase();
          else
            return graderCompany.toUpperCase().trim().substring(0, maxChars);
        }
        else
            return ''
    } else {
        return ''
    }
}


// Funzione che costruisce la stringa con le extra info che andranno stampate sull'etichetta
export function parseExtraInfoToPrintOnLabel (extraInfo, maxChars) {
    return isDefined(extraInfo) ? extraInfo.substring(0, maxChars) : '';
}


// Funzione che costruisce la stringa 'Authentic' che andrà stampata sull'etichetta
export function parseAuthenticToPrintOnLabel (authentic, qualifier, labelType, labelLanguage) {
    return (authentic + (isQualifierPresent(qualifier) ? ' - ' + parseQualifierToPrintOnLabel(false, false, qualifier, maxCharsForQualifier(labelType, labelLanguage)) : '')).toUpperCase();
}


// Funzione che costruisce la stringa Case Fresh o Authentic che andrà stampata sull'etichetta di un prodotto sealed gradato
export function parseSealedCaseFreshAuthenticToPrintOnLabel (isCaseFresh, caseFresh, authentic, qualifier) {
    return ((isCaseFresh ? caseFresh : authentic) + (isQualifierPresent(qualifier) ? ' - ' + qualifier : '')).toUpperCase()
}


// Funzione che costruisce la stringa composta dalle extra info e dalla stringa heavy / light per un prodotto sealed
// che andrà stampata sull'etichetta
export function parseExtraInfoAndHeavyLightToPrintOnLabel (extraInfo, heavyLight, sealed_informations_obj, maxChars) {
    if (isDefined(extraInfo) && isDefined(heavyLight)) {
        return (extraInfo.substring(0, maxChars - (' - ' + sealed_informations_obj[heavyLight]).length) + ' - ' + titleize(sealed_informations_obj[heavyLight]));
    }
    else if (isDefined(extraInfo)) {
        return parseExtraInfoToPrintOnLabel (extraInfo, maxChars)
    }
    else if (isDefined(heavyLight) && isDefined(sealed_informations_obj[heavyLight])) {
        return titleize(sealed_informations_obj[heavyLight])
    }
    else {
        return '';
    }
}


// Funzione che costruisce la stringa composta dal gradeOverall della carta / prodotto sealed gradato che andrà stampata
// in etichetta
export function parseGradeOverallToPrintOnLabel (isCaseFresh, isAuthentic, grade_overall) {
    if (isCaseFresh || isAuthentic)
        return ['', '']
    else {
        // Se il valore è 10.5 (10 Pristine), mostra "10" invece di "10.5"
        if (grade_overall === '10.5' || grade_overall === 10.5) {
            return ['10', '']
        }
        
        const gradeOverall = grade_overall.toString().replace('.0', '').split('.')
        if (gradeOverall.length > 1)
            return [gradeOverall[0], '.' + gradeOverall[1]]
        else
            return [gradeOverall[0], '']
    }
}


// Funzione che costruisce la stringa composta dal qualifier della carta / prodotto sealed gradato che andrà stampata
// in etichetta
export function parseQualifierToPrintOnLabel (isCaseFresh, isAuthentic, qualifier, maxChars, notUpperCase) {
    if (isCaseFresh || isAuthentic)
        return ''
    else if (isQualifierPresent(qualifier))
        return finalParse(qualifier.trim().substring(0, maxChars), notUpperCase);
    else
      return '';
}




// PARSER DELLE STRINGHE DA STAMPARE NELLE NUOVE ETICHETTE AIGRADING (QUELLE DI TIPO RADIANT)

// Funzione che costruisce le stringhe da stampare sul back delle etichette radiant che riportano i vari sub-grades
export function parseBackLinesToPrintOnLabel (isSingleCard, isBoosterPack, gradeSurfaceOverall, gradeSealedSurface, gradeSealing,
                                              gradeEdgesOverall, gradeCornersOverall, gradeFlatness, gradeSealedCorners,
                                              gradeCenteringOverall, gradeCrimping, gradeHangingHole, gradeMaterialCondition,
                                              centeringSubGrades, extraInfo, maxChars, isAuthentic, isAuthenticWithSubgrades, isCaseFresh,
                                              isGrading, isGradingWithSubGrades, is10Perfect, blueprintInfo2PrintOnLabel, qualifiersToPrintOnLabel, qualifier) {

  let lines = [];
  if (isSingleCard) {
    if (isGradingWithSubGrades || (isGrading && is10Perfect) || (!isAuthentic && !isGrading && !isGradingWithSubGrades)) {
      // Se è grading semplice con 10 Pristine, usa valori fissi di 10 per tutti i subgrades
      const surfaceValue = gradeSurfaceOverall;
      const edgesValue = gradeEdgesOverall;
      const cornersValue = gradeCornersOverall;
      const centeringValue = (!centeringSubGrades || centeringSubGrades.filter(g => g === 'na').length === 4 ? 'N/A' : gradeCenteringOverall);
      
      const centeringLabel =
        ['miscut', 'tilted'].includes(qualifier?.toLowerCase()) && isDefined(qualifiersToPrintOnLabel?.left?.translation) ?
          qualifiersToPrintOnLabel.left.translation
        :
          stringifyGrading(centeringValue);
      lines = [
        `Surface: ${stringifyGrading(surfaceValue)}`,
        `Edges: ${stringifyGrading(edgesValue)}`,
        `Corners: ${stringifyGrading(cornersValue)}`,
        `Centering: ${centeringLabel}`,
        parseExtraInfoToPrintOnLabel(extraInfo, maxChars)
      ]
    }
    else if ((isAuthentic || isGrading) && isDefined(extraInfo)) {
      lines = [
        parseExtraInfoToPrintOnLabel(extraInfo, maxChars)
      ]
    }
  } else if ((isGradingWithSubGrades || (isGrading && is10Perfect) || (isAuthenticWithSubgrades && !isCaseFresh))) {
    if (isBoosterPack) {
      lines = [
        `Surface: ${stringifyGrading(gradeSealedSurface)}`,
        `Sealing: ${stringifyGrading(gradeSealing)}`,
        `Flatness: ${titleize(gradeFlatness.replaceAll('_', ' '))}`,
        `Crimping: ${titleize(gradeCrimping.replaceAll('_', ' '))}`,
        gradeHangingHole ? `Hanging Hole: ${titleize(gradeHangingHole.replaceAll('_', ' '))}` : null
      ]
    } else {
      lines = [
        `Sealing: ${stringifyGrading(gradeSealing)}`,
        `Surface: ${stringifyGrading(gradeSealedSurface)}`,
        `Corners: ${stringifyGrading(gradeSealedCorners)}`,
        gradeHangingHole ? `Hanging Hole: ${titleize(gradeHangingHole.replaceAll('_', ' '))}` : null,
        gradeMaterialCondition ? `Material: ${titleize(gradeMaterialCondition.replaceAll('_', ' '))}` : null
      ]
    }
  }


  const backLines = {};
  let index = 5;
  lines = lines.filter(l => isDefined(l))
  for (let i = lines.length - 1; i >= 0; i--) {
    backLines[`label_back_line_${index}`] = lines[i];
    index = index - 1;
  }

  return backLines;
}




// Funzione che effettua un parsing finale a una stringa da stampare in etichetta
export function finalParse(string, notUpperCase) {
  return notUpperCase ? string : string?.toUpperCase();
}


// Funzione che ritorna il numero massimo di caratteri usabili per la stampa in etichetta, tenendo conto del tipo di
// etichetta scelta e della lingua di stampa
export function maxCharsByLocale (labelLanguage, labelType, stringType) {
  if (isDefined(labelLanguage) && isDefined(labelType)) {
    if (labelType === 'legacy')
      return (IDEOGRAMS_LANGUAGES.includes(labelLanguage) ? MAX_CHARS_FOR_ASIATIC_LEGACY : MAX_CHARS_FOR_LEGACY);
    else {
      if (stringType?.toLowerCase() !== 'productname')
        return (IDEOGRAMS_LANGUAGES.includes(labelLanguage) ? MAX_CHARS_FOR_ASIATIC_RADIANT : MAX_CHARS_FOR_RADIANT);
      else
        return (IDEOGRAMS_LANGUAGES.includes(labelLanguage) ? MAX_CHARS_FOR_ASIATIC_PRODUCT_NAME_RADIANT : MAX_CHARS_FOR_PRODUCT_NAME_RADIANT);
    }
  } else
    charsUpperLimit();
}


// Funzione che ritorna il numero massimo di caratteri usabili per la stampa in etichetta, tenendo conto unicamente
// della lingua di stampa della stessa
export function charsUpperLimit (labelLanguage, stringType) {
  if (isDefined(labelLanguage))
    return Math.max(maxCharsByLocale(labelLanguage, 'legacy', stringType), maxCharsByLocale(labelLanguage, 'radiant', stringType))
  else
    return Math.max(MAX_CHARS_FOR_LEGACY, MAX_CHARS_FOR_RADIANT, MAX_CHARS_FOR_ASIATIC_LEGACY, MAX_CHARS_FOR_ASIATIC_RADIANT);
}


// Funzione che ritorna il limnite di caratteri più basso stampabili in etichetta, tenendo conto unicamente
// della lingua di stampa della stessa
export function charsLowerLimit (labelLanguage, stringType) {
  if (isDefined(labelLanguage))
    return Math.min(maxCharsByLocale(labelLanguage, 'legacy', stringType), maxCharsByLocale(labelLanguage, 'radiant'))
  else
    return Math.min(MAX_CHARS_FOR_LEGACY, MAX_CHARS_FOR_RADIANT, MAX_CHARS_FOR_ASIATIC_LEGACY, MAX_CHARS_FOR_ASIATIC_RADIANT);
}


// Funzione che ritorna il numero massimo di caratteri usabili per la stampa della stringa del qualifier sull'etichetta,
// tenendo conto del tipo di etichetta scelta e della lingua di stampa
export function maxCharsForQualifier (labelType, labelLanguage) {
  if (labelType === 'legacy')
    return MAX_CHARS_FOR_QUALIFIER_LEGACY;
  else
    return IDEOGRAMS_LANGUAGES.includes(labelLanguage) ? MAX_CHARS_FOR_ASIATIC_RADIANT : MAX_CHARS_FOR_RADIANT;
}